import { useState } from 'react'
import { Button } from '../../ui/button'
import { CardImageTextExt } from '../../ui/card-image-text-ext'
import { Icon } from '../../ui/icon'
import { InfoTooltip } from '../../ui/info-tooltip'
import { Tag } from '../../ui/tag'

interface PreAutoStepScreenProps {
	onNext: (data?: Record<string, any>) => void
	onBack: () => void
	data: Record<string, any>
}

const coveragePlans = [
	{
		id: 'ceibo',
		name: 'Plan Ceibo',
		description: 'Cobertura básica y esencial.',
		tag: 'Responsabilidad civil',
		image: '/img/cotizacion/ceibo.png',
	},
	{
		id: 'jacaranda',
		name: 'Plan Jacarandá',
		description: 'Cobertura sólida.',
		tag: 'Terceros',
		image: '/img/cotizacion/jacaranda.png',
	},
	{
		id: 'roble',
		name: 'Plan Roble',
		description: 'Cobertura extendida.',
		tag: 'Terceros full',
		image: '/img/cotizacion/roble.png',
	},
	{
		id: 'algarrobo',
		name: 'Plan Algarrobo',
		description: 'La cobertura más completa.',
		tag: 'Franquicia',
		image: '/img/cotizacion/algarrobo.png',
	},
]

export function PreAutoStepScreen({
	onNext,
	onBack,
	data,
}: PreAutoStepScreenProps) {
	const [selectedPlan, setSelectedPlan] = useState<string | null>(
		data.selectedPlan || null,
	)

	const handleContinue = () => {
		if (selectedPlan) {
			onNext({ selectedPlan })
		}
	}

	return (
		<div className="flex w-full flex-col gap-6 pb-20 md:gap-8 md:pb-0">
			{/* Header with title and Sello Verde badge */}
			<div className="flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
				<h2 className="font-parkinsans text-[20px] leading-7 font-medium text-blue-800 md:text-[24px] md:leading-8">
					Elige el tipo de cobertura
				</h2>

				{/* Sello Verde badge with tooltip */}
				<InfoTooltip
					title="¿Qué es el sello verde?"
					content={
						<>
							<p className="mb-1">
								Nuestros planes suman acciones de impacto ambiental:
							</p>
							<ul className="ml-[18px] list-disc space-y-0">
								<li>Compensación de huella de carbono.</li>
								<li>Apoyo a programas de educación ambiental.</li>
								<li>Aporte a energías limpias.</li>
							</ul>
						</>
					}
				>
					<Tag
						variant="secondary"
						appearance="solid"
						color="green"
						size="l"
						corner="rounded"
						option="custom"
						className="h-auto gap-2 rounded-lg px-2 py-2"
					>
						<div className="flex items-center gap-1">
							<Icon
								name="natural-food"
								className="size-[18px] text-green-500"
							/>
							<p className="font-parkinsans text-base leading-5 font-medium text-green-500">
								Sello Verde
							</p>
						</div>
						<Icon
							name="question-mark-circled"
							className="size-4 text-green-600"
						/>
					</Tag>
				</InfoTooltip>
			</div>

			{/* Cards Container - Vertical on mobile, horizontal on desktop */}
			<div className="flex h-auto w-full flex-col gap-3 md:h-[380px] md:flex-row md:gap-2">
				{coveragePlans.map((plan) => (
					<div key={plan.id} className="contents">
						{/* Mobile: standard horizontal layout */}
						<CardImageTextExt
							variant="standard"
							layout="horizontal"
							state={selectedPlan === plan.id ? 'selected' : 'default'}
							clickable
							onCardClick={() => setSelectedPlan(plan.id)}
							className="w-full rounded-[16px] md:hidden"
							showImage
							image={plan.image}
							imageAlt={plan.name}
							showChip
							chipText={plan.tag}
							title={plan.name}
							showDescription
							description={plan.description}
							showButton={false}
							showButton2={false}
							showIcon={false}
						/>
						{/* Desktop: coverage vertical layout */}
						<CardImageTextExt
							variant="coverage"
							state={selectedPlan === plan.id ? 'selected' : 'default'}
							clickable
							onCardClick={() => setSelectedPlan(plan.id)}
							className="hidden w-full rounded-2xl md:flex md:flex-1"
							showImage
							image={plan.image}
							imageAlt={plan.name}
							showChip
							chipText={plan.tag}
							title={plan.name}
							showDescription
							description={plan.description}
							showButton={false}
							showButton2={false}
							showIcon={false}
						/>
					</div>
				))}
			</div>

			{/* Footer buttons - Desktop only */}
			<div className="hidden w-full flex-col-reverse items-center justify-between gap-3 md:flex md:flex-row md:gap-0">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="primary"
					appearance="solid"
					size="l"
					onClick={handleContinue}
					disabled={!selectedPlan}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>

			{/* Mobile footer - Fixed at bottom */}
			<div className="fixed right-0 bottom-0 left-0 z-10 border-t border-neutral-400 bg-white px-6 py-3 md:hidden">
				<div className="flex items-center justify-between">
					<Button
						variant="primary"
						appearance="ghost"
						size="m"
						onClick={onBack}
						className="gap-2 rounded-xl px-4 py-3"
					>
						<Icon name="arrow-left" className="size-4" />
						Atrás
					</Button>
					<Button
						variant="primary"
						appearance="solid"
						size="m"
						onClick={handleContinue}
						disabled={!selectedPlan}
						className="gap-2 rounded-xl px-4 py-3"
					>
						Siguiente
						<Icon name="arrow-right" className="size-3.5" />
					</Button>
				</div>
			</div>
		</div>
	)
}
